import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ClientImportAggregation,
  type ProductClientsImportAggregation,
  type ProductImportSummary,
  type RetryProductImportResponse,
} from "@fitness-app/data-models/entities/ProductClientToImport";

import { productApi } from "~/store/initializeStore";

const IN_PROGRESS_POLL_MS = 3000;

export const productImportsQueryKey = (productId: string) => ["productImports", productId] as const;
export const productImportQueryKey = (productId: string, importId: string) =>
  ["productImport", productId, importId] as const;

export const useProductImports = (productId: string, enabled = true) => {
  return useQuery<ProductClientsImportAggregation[]>({
    queryKey: productImportsQueryKey(productId),
    enabled: enabled && Boolean(productId),
    queryFn: async () => {
      const { data } = await productApi.get<ProductClientsImportAggregation[]>(`/products/${productId}/imports`);

      return data;
    },
    // A list opened while an import runs keeps moving until it finishes.
    refetchInterval: (data) =>
      data?.some((item) => item.status === ClientImportAggregation.InProgress) ? IN_PROGRESS_POLL_MS : false,
  });
};

export const useProductImport = (productId: string, importId: string | null) => {
  return useQuery<ProductImportSummary>({
    queryKey: productImportQueryKey(productId, importId ?? ""),
    enabled: Boolean(productId && importId),
    queryFn: async () => {
      const { data } = await productApi.get<ProductImportSummary>(`/products/${productId}/imports/${importId}`);

      return data;
    },
    refetchInterval: (data) =>
      data?.aggregation.status === ClientImportAggregation.InProgress ? IN_PROGRESS_POLL_MS : false,
  });
};

export const useRetryProductImport = (productId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (importId: string) => {
      const { data } = await productApi.post<RetryProductImportResponse>(
        `/products/${productId}/imports/${importId}/retry`,
      );

      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productImportsQueryKey(productId) }),
  });
};
